import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { trackAndNavigate } from "../utils/navigate";
import {
  ShieldCheck,
  UserPlus,
  ClipboardList,
  Video,
  Pill,
  HelpCircle,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const steps = [
  {
    id: 1,
    title: "Check Your Eligibility",
    color: "bg-gradient-to-br from-[#005eb8] to-[#00aeef]",
    icon: <ShieldCheck className="w-10 h-10 text-white" />,
    detail:
      "Complete our short eligibility questionnaire. It takes less than 30 seconds and is completely free. Based on your responses, we will indicate whether you may be suitable to proceed with an application. Completing the eligibility check does not commit you to anything.",
  },
  {
    id: 2,
    title: "Create a Patient Account",
    color: "bg-gradient-to-br from-[#2d8c6e] to-[#4caf50]",
    icon: <UserPlus className="w-10 h-10 text-white" />,
    detail:
      "If your eligibility check suggests you may be suitable, you will be invited to create a secure patient account on our portal. Registration is free and does not incur any charges.",
  },
  {
    id: 3,
    title: "Complete Your Profile",
    color: "bg-gradient-to-br from-[#2ba78c] to-[#76c042]",
    icon: <ClipboardList className="w-10 h-10 text-white" />,
    detail:
      "Once registered, you will complete your patient profile. This includes your personal details, a summary of your medical history, details of your diagnosed condition, and information about treatments you have previously tried.",
  },
  {
    id: 4,
    title: "Initial Consultation",
    color: "bg-gradient-to-br from-[#012d5c] to-[#1a2e4a]",
    icon: <Video className="w-10 h-10 text-white" />,
    detail:
      "Your initial consultation takes place via a secure video call with one of our specialist clinicians. They will review your medical history, discuss your symptoms, and explain the clinical assessment process. Initial consultations are provided at no charge.",
  },
  {
    id: 5,
    title: "Prescription Issued Following MDT Review",
    color: "bg-gradient-to-br from-[#4caf50] to-[#2d8c6e]",
    icon: <Pill className="w-10 h-10 text-white" />,
    detail:
      "Following your consultation, your case is reviewed by our multi-disciplinary team (MDT). The MDT independently considers all clinical information before making a prescribing decision. A prescription is only issued where clinically approved by the MDT. Not all patients who consult will receive a prescription.",
  },
];

const infoNeeded = [
  "Personal details and contact information",
  "A valid form of ID",
  "Details of your diagnosed condition(s)",
  "A summary of your current and previous medications",
  "A record of treatments you have already tried",
  "Supporting medical documentation where available (e.g. GP letters, clinic correspondence)",
];

const HowItWorksPage = () => {
  useEffect(() => {
    document.title = "How It Works | Integro Clinics";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20 lg:py-28"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <Reveal>
            <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              Patient Journey
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              How It Works
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
              A clear, step-by-step guide to the Integro patient journey — from your initial eligibility check through to clinical assessment and review.
            </p>
            <Button
              className="bg-[#4caf50] hover:bg-[#3d8b40] text-white px-8 py-6 rounded-lg text-lg font-bold shadow-lg"
              onClick={() => trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' })}
            >
              Check Your Eligibility
            </Button>
          </Reveal>
        </div>
      </div>

      {/* The 5 Steps */}
      <section className="py-20 bg-[#f0f4f8]">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#012d5c] mb-4">
                Your Patient Journey
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Every patient follows the same clear pathway. Here is what to expect at each stage.
              </p>
            </div>
          </Reveal>

          <div className="max-w-4xl mx-auto space-y-6">
            {steps.map((step) => (
              <Reveal key={step.id}>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col md:flex-row">
                  <div
                    className={`${step.color} md:w-28 flex items-center justify-center p-6 shrink-0`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      {step.icon}
                      <span className="text-white font-bold text-sm">
                        Step {step.id}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex-1">
                    <h3 className="text-xl font-bold text-[#012d5c] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{step.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-8 max-w-4xl mx-auto bg-white border border-gray-200 rounded-xl p-6 text-center">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Please note:</strong> Completing each stage does not guarantee progression to the next. Prescription issuance is subject to MDT review and clinical approval. A prescription is not guaranteed.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What happens after your consultation? */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal>
            <h2 className="text-3xl font-bold text-[#012d5c] mb-6">
              What Happens After Your Consultation?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              After your initial consultation, your case is passed to our multi-disciplinary team (MDT) for independent clinical review. The MDT includes specialist clinicians with relevant expertise who consider all available clinical information before any prescribing decision is made.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              The MDT review ensures that prescribing decisions are made carefully, responsibly, and in the best interests of each individual patient. You will be informed of the outcome of the review by our clinical team.
            </p>
            <div className="bg-[#e0f2fe]/50 border-l-4 border-[#005eb8] p-6 rounded-r-lg">
              <p className="text-[#012d5c] font-medium">
                MDT review is a standard part of responsible clinical practice at Integro. No single clinician makes prescribing decisions in isolation.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Do I automatically receive a prescription? */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal>
            <div className="flex items-start gap-4">
              <div className="bg-[#fff3cd] p-3 rounded-xl shrink-0 mt-1">
                <AlertCircle className="w-6 h-6 text-[#856404]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#012d5c] mb-4">
                  Do I Automatically Receive a Prescription?
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  No. A prescription is not automatically issued following a consultation.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Issuing a medical cannabis prescription is a clinical decision made only after MDT review and approval. Not all patients who complete an initial consultation will receive a prescription. Our clinical team will always act in your best clinical interest.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  The prescribing clinician determines whether treatment is clinically appropriate, based on the patient's individual circumstances and clinical history. Eligibility does not guarantee that treatment will be recommended or prescribed.
                </p>
                <div className="bg-[#e8f5e9] border border-[#4caf50]/30 p-5 rounded-xl">
                  <p className="text-[#1b5e20] font-medium text-sm leading-relaxed">
                    If the MDT determines that medical cannabis treatment is not appropriate at this time, our clinical team will explain the reasons and discuss what alternative options may be available.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What information will I need? */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal>
            <h2 className="text-3xl font-bold text-[#012d5c] mb-6">
              What Information Will I Need?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              To complete your application and allow our clinical team to assess your case thoroughly, you will typically need the following:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {infoNeeded.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 bg-[#f8fafc] p-4 rounded-xl border border-gray-100"
                >
                  <CheckCircle className="w-5 h-5 text-[#4caf50] shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm leading-relaxed">
                    {item}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-gray-500">
              Our team will guide you through exactly what is required for your specific case.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What if I'm not eligible? */}
      <section className="py-20 bg-[#f0f4f8]">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal>
            <h2 className="text-3xl font-bold text-[#012d5c] mb-6">
              What If I'm Not Eligible?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              If our eligibility check suggests you may not be suitable for treatment at Integro at this time, we will explain why. This is not a reflection on you — eligibility criteria exist to ensure that assessments are focused on patients where a specialist review is clinically appropriate.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              If you are not eligible, we would encourage you to speak with your GP about alternative treatment options. If your circumstances change in the future — for example, if you are diagnosed with a qualifying condition — you are welcome to complete our eligibility check again at any time.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Eligibility decisions are made on clinical grounds only. Our eligibility check is free, takes under 30 seconds, and does not commit you to anything.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Questions about the process? */}
      <section className="py-20 bg-[#012d5c] text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Reveal>
            <HelpCircle className="w-12 h-12 text-[#4caf50] mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Questions About the Process?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              Our team is here to help. Explore our FAQ, get in touch directly, or start your eligibility check today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-[#4caf50] hover:bg-[#3d8b40] text-white px-8 py-6 rounded-lg text-lg font-bold"
                onClick={() => trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' })}
              >
                Check Your Eligibility
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#012d5c] px-8 py-6 rounded-lg text-lg"
                asChild
              >
                <Link to="/faq">View FAQs</Link>
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#012d5c] px-8 py-6 rounded-lg text-lg"
                onClick={() =>
                  window.location.href = "mailto:office@integroclinics.com"
                }
              >
                Contact Us
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
